import { useEffect, useRef, useState } from 'react';
import { Box, Button, TextField } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { format } from 'date-fns';

/** A view's link, as partners open it. */
export const shareLink = (token) => `${window.location.origin}/s/${token}`;
/** A set-password link from the server's path. */
export const fullLink = (path) => `${window.location.origin}${path}`;

export const when = (iso, empty = 'Never') => (iso ? format(new Date(iso), 'd MMM yyyy, HH:mm') : empty);

export const partnerName = (p) => [p.first_name, p.last_name].filter(Boolean).join(' ') || p.username;

/**
 * Marks a page opened by link as private while it's shown: search engines are told not to index
 * it, and no referrer (which would carry the link) is sent to anything it loads.
 */
export const usePrivatePage = (title) => {
  useEffect(() => {
    const added = [['robots', 'noindex, nofollow'], ['referrer', 'no-referrer']].map(([name, content]) => {
      const meta = document.createElement('meta');
      meta.name = name;
      meta.content = content;
      document.head.appendChild(meta);
      return meta;
    });
    return () => added.forEach((meta) => meta.remove());
  }, []);
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);
};

/** A read-only value with a Copy button. If the clipboard is blocked, the text is selected to copy by hand. */
export const CopyField = ({ value, label }) => {
  const input = useRef(null);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      input.current?.select();
    }
  };
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <TextField size="small" fullWidth label={label} value={value} inputRef={input}
        InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
        onFocus={(e) => e.target.select()} />
      <Button variant="outlined" startIcon={<ContentCopyRoundedIcon />} onClick={copy}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </Box>
  );
};
